import { useCallback, useEffect, useRef, useState } from 'react';
import { aiActionsApi } from '../../api/aiActionsApi';
import { STATUS_LABEL, aiActionExplanation, toAiAction } from '../../redesign/data/aiAction';
import type { AiAction } from '../../redesign/data/types';

/**
 * Массовое действие или удаление Штаба в прежнем чате (S14, SC08).
 *
 * Пока новый интерфейс за флагом, большинство тренеров пишут Штабу здесь.
 * Массовые изменения сервер больше не исполняет сразу, поэтому без этой
 * карточки их нечем было бы подтвердить. Точный список виден целиком до
 * подтверждения; повторное нажатие сервер не исполняет второй раз.
 */
export function PendingActionCard({ id }: { id: string }) {
  const [action, setAction] = useState<AiAction | null>(null);
  const [loadError, setLoadError] = useState<'not_found' | 'network' | null>(null);
  const [busy, setBusy] = useState<'confirm' | 'cancel' | null>(null);
  const [decision, setDecision] = useState<{ replayed: boolean } | null>(null);
  const [failed, setFailed] = useState(false);
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setAction(toAiAction(await aiActionsApi.get(id)));
    } catch (err) {
      const status = (err as { status?: number } | undefined)?.status;
      setLoadError(status === 404 ? 'not_found' : 'network');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = async (kind: 'confirm' | 'cancel') => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(kind);
    setFailed(false);
    try {
      const res = kind === 'confirm' ? await aiActionsApi.confirm(id) : await aiActionsApi.cancel(id);
      setAction(toAiAction(res));
      setDecision({ replayed: !!res.replayed });
    } catch {
      setFailed(true);
    } finally {
      inFlight.current = false;
      setBusy(null);
    }
  };

  const box = 'mt-3 rounded-xl p-3 text-sm';
  const boxStyle = { backgroundColor: 'var(--bg-secondary, rgba(0,0,0,0.04))', color: 'var(--text-primary)' };

  if (loadError) {
    return (
      <div className={box} style={boxStyle}>
        {loadError === 'not_found' ? (
          <p>Действие недоступно. Ничего не изменено.</p>
        ) : (
          <>
            <p>Действие не загрузилось.</p>
            <button type="button" className="mt-2 underline" onClick={() => void load()}>
              Повторить
            </button>
          </>
        )}
      </div>
    );
  }
  if (!action) {
    return (
      <div className={box} style={boxStyle} role="status">
        Загружаю действие
      </div>
    );
  }

  const decidedHere = !!decision && !decision.replayed;
  return (
    <div className={box} style={boxStyle}>
      <p className="font-semibold">
        {action.title} - {STATUS_LABEL[action.status].toLowerCase()}
      </p>
      {action.summary && <p style={{ color: 'var(--text-secondary)' }}>{action.summary}</p>}
      <ul className="mt-2 space-y-1">
        {action.items.map((it, i) => (
          <li key={`${it.label}-${i}`}>
            <span className="font-medium">{it.label}</span>: {it.before} → {it.after}
          </li>
        ))}
      </ul>
      {action.note &&
        action.note.split('\n').map((line, i) => (
          <p key={i} className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            {line}
          </p>
        ))}
      <p className="mt-2" role="status">
        {aiActionExplanation(action, { decidedHere, replayed: decision?.replayed })}
      </p>
      {action.result && action.result.failed.length > 0 && (
        <ul className="mt-1" style={{ color: 'var(--danger)' }}>
          {action.result.failed.map((it, i) => (
            <li key={`${it.label}-${i}`}>
              {it.label}
              {it.detail ? `: ${it.detail}` : ''}
            </li>
          ))}
        </ul>
      )}
      {failed && (
        <p className="mt-2" role="alert" style={{ color: 'var(--danger)' }}>
          Нет связи с сервером. Повторить безопасно - дважды действие не выполнится.
        </p>
      )}
      {action.status === 'pending' && (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            disabled={!!busy}
            onClick={() => void decide('confirm')}
            className="px-4 py-2 rounded-lg font-medium active:opacity-70 disabled:opacity-50"
            style={{ backgroundColor: action.danger ? 'var(--danger)' : 'var(--accent)', color: '#FFFFFF', minHeight: 44 }}
          >
            {busy === 'confirm' ? 'Выполняю' : action.confirmLabel}
          </button>
          <button
            type="button"
            disabled={!!busy}
            onClick={() => void decide('cancel')}
            className="px-4 py-2 rounded-lg font-medium active:opacity-70 disabled:opacity-50"
            style={{ border: '1px solid var(--separator)', minHeight: 44 }}
          >
            {busy === 'cancel' ? 'Отменяю' : 'Отмена'}
          </button>
        </div>
      )}
    </div>
  );
}
